import React, { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import CoverScreen from "@/components/CoverScreen";
import InvitationCard from "@/components/InvitationCard";
import CountdownTimer from "@/components/CountdownTimer";
import MapCard from "@/components/MapCard";
import Footer from "@/components/Footer";
import LanguageToggle from "@/components/LanguageToggle";
import { useLang } from "@/i18n";
import { usePageMeta } from "@/hooks/usePageMeta";
import { applyTheme } from "@/themes/contract";
import { getTheme, themeList } from "@/themes/registry";
import {
    SAMPLE_GUEST,
    SAMPLE_MARK,
    SAMPLE_WEDDING,
    previewDescription,
    previewTitle,
} from "@/themes/sampleGallery";

function nextTheme(theme) {
    if (!themeList.length) return theme;
    const index = themeList.findIndex((item) => item.id === theme.id);
    const start = index >= 0 ? index : 0;
    return themeList[(start + 1) % themeList.length];
}

export default function ThemePreviewPage() {
    const { themeId } = useParams();
    const { dir } = useLang();
    const theme = getTheme(themeId);
    const next = nextTheme(theme);
    const invitationRef = useRef(null);
    const NextIcon = dir === "rtl" ? ArrowLeft : ArrowRight;

    usePageMeta(previewTitle(theme), previewDescription(theme));

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [themeId]);

    const handleOpen = () => {
        invitationRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div
            dir={dir}
            className="wd-theme wd-paper-texture min-h-[100dvh]"
            style={applyTheme(theme)}
            data-ornament={theme.ornament}
            data-testid="theme-preview"
            data-theme-id={theme.id}
        >
            <header className="sticky top-0 z-40 border-b border-[var(--wd-border)] bg-[var(--wd-bg)]/95 backdrop-blur">
                <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <Link
                        to="/themes"
                        className="shrink-0 text-sm text-[var(--wd-primary)] hover:text-[var(--wd-accent)]"
                        data-testid="theme-back"
                    >
                        كل السمات / All themes
                    </Link>
                    <p className="text-sm font-medium text-[var(--wd-accent)]" data-testid="sample-mark">
                        {SAMPLE_MARK}
                    </p>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <Link
                            to={`/themes/${next.id}`}
                            className="inline-flex min-w-0 items-center gap-1 text-sm text-[var(--wd-primary)] hover:text-[var(--wd-accent)]"
                            data-testid="theme-next"
                        >
                            <span>التالي / Next</span>
                            <span translate="no">{` · ${next.name}`}</span>
                            <NextIcon size={14} aria-hidden="true" className="shrink-0" />
                        </Link>
                        <LanguageToggle className="shrink-0 !bg-[var(--wd-bg)] !text-[var(--wd-text)] hover:!bg-[var(--wd-bg-alt)]" />
                    </div>
                </div>
            </header>

            <CoverScreen
                wedding={SAMPLE_WEDDING}
                guestName={SAMPLE_GUEST.name}
                englishGuestName={SAMPLE_GUEST.english_name}
                onOpen={handleOpen}
            />

            <div ref={invitationRef}>
                <InvitationCard wedding={SAMPLE_WEDDING} guestName={SAMPLE_GUEST.name} />
                <CountdownTimer dateIso={SAMPLE_WEDDING.date_iso} />
                <MapCard wedding={SAMPLE_WEDDING} />
                <Footer wedding={SAMPLE_WEDDING} />
            </div>
        </div>
    );
}
